import { useState, useEffect, useCallback } from 'react'
import {
  ChevronLeft, ChevronRight, Plus, Calendar, List, MapPin, X,
  Clock, Edit2, Trash2, User, Footprints, Building, Users,
  Megaphone, Car, Mic, UtensilsCrossed, PartyPopper, FileText,
} from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Badge, Card, Modal, ModalHeader, ModalBody, ModalFooter, EmptyState, SlidePanel } from '../../../components/ui'

interface Activity {
  id: number
  date: string
  title: string
  description: string | null
  time_start: string | null
  time_end: string | null
  type_code: string
  type_label: string
  status_code: string
  status_label: string
  status_color: string
  city_name: string | null
  state_code: string | null
  neighborhood: string | null
  location_name: string | null
  address: string | null
  latitude: number | null
  longitude: number | null
  assigned_name: string | null
  notes: string | null
  activity_type_id: number
  status_type_id: number
}

interface ActivityType { id: number; code: string; label: string }
interface StatusType { id: number; code: string; label: string; color: string }

const MONTHS = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']
const WEEKDAYS = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb']

const statusBadge: Record<string, 'brand' | 'success' | 'error' | 'gray' | 'info'> = {
  PLANNED: 'info', CONFIRMED: 'brand', DONE: 'success', CANCELLED: 'gray',
}

const typeIconMap: Record<string, React.ElementType> = {
  CAMINHADA: Footprints, VISITA: Building, REUNIAO: Users,
  COMICIO: Megaphone, CARREATA: Car, ENTREVISTA: Mic,
  ALMOCO: UtensilsCrossed, EVENTO: PartyPopper, OUTRO: FileText,
}

export default function AgendaPage() {
  const [view, setView] = useState<'month' | 'list'>('month')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [activities, setActivities] = useState<Activity[]>([])
  const [types, setTypes] = useState<ActivityType[]>([])
  const [statuses, setStatuses] = useState<StatusType[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null)
  const [viewingActivity, setViewingActivity] = useState<Activity | null>(null)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [dayDetailDate, setDayDetailDate] = useState<string | null>(null)

  // Form state
  const [form, setForm] = useState({
    date: '', title: '', description: '', time_start: '', time_end: '',
    activity_type_id: '', neighborhood: '', address: '', location_name: '',
    notes: '', status_type_id: '',
  })

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  const fetchActivities = useCallback(async () => {
    setLoading(true)
    try {
      const startDate = new Date(year, month, 1)
      const endDate = new Date(year, month + 1, 0)
      const res = await axios.get(buildApiUrl('/v1/agenda'), {
        params: {
          start_date: fmt(startDate),
          end_date: fmt(endDate),
        },
      })
      setActivities(res.data.data)
    } catch { /* ignore */ } finally {
      setLoading(false)
    }
  }, [year, month])

  const fetchTypes = async () => {
    try {
      const [t, s] = await Promise.all([
        axios.get(buildApiUrl('/v1/agenda/types')),
        axios.get(buildApiUrl('/v1/agenda/statuses')),
      ])
      setTypes(t.data.data)
      setStatuses(s.data.data)
    } catch { /* ignore */ }
  }

  useEffect(() => { fetchTypes() }, [])
  useEffect(() => { fetchActivities() }, [fetchActivities])

  // Helpers
  function fmt(d: Date) { return d.toISOString().split('T')[0] }
  function fmtTime(t: string | null) { return t ? t.substring(0, 5) : '' }

  function prevMonth() { setCurrentDate(new Date(year, month - 1, 1)) }
  function nextMonth() { setCurrentDate(new Date(year, month + 1, 1)) }
  function goToday() { setCurrentDate(new Date()) }

  function getActivitiesForDate(date: string) {
    return activities.filter(a => a.date === date)
  }

  // Calendar grid
  function getCalendarDays() {
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const days: { date: string; day: number; isCurrentMonth: boolean }[] = []

    // Previous month
    const prevDays = new Date(year, month, 0).getDate()
    for (let i = firstDay - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevDays - i)
      days.push({ date: fmt(d), day: prevDays - i, isCurrentMonth: false })
    }
    // Current month
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(year, month, i)
      days.push({ date: fmt(d), day: i, isCurrentMonth: true })
    }
    // Next month
    const remaining = 42 - days.length
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i)
      days.push({ date: fmt(d), day: i, isCurrentMonth: false })
    }
    return days
  }

  // Open modal
  function openCreate(date?: string, timeStart?: string) {
    const plannedId = statuses.find(s => s.code === 'PLANNED')?.id
    const targetDate = date || fmt(new Date())

    let endTime = ''
    if (timeStart) {
      const startMin = parseInt(timeStart.split(':')[0]) * 60 + parseInt(timeStart.split(':')[1])
      const defaultEnd = startMin + 60 // +1h padrão

      // Buscar próxima atividade depois desse horário
      const dayActs = getActivitiesForDate(targetDate)
      const nextActivity = dayActs
        .filter(a => a.time_start && timeToMinutes(a.time_start) > startMin)
        .sort((a, b) => timeToMinutes(a.time_start!) - timeToMinutes(b.time_start!))
        [0]

      // End = menor entre +1h e início da próxima atividade
      const maxEnd = nextActivity ? timeToMinutes(nextActivity.time_start!) : defaultEnd
      const finalEnd = Math.min(defaultEnd, maxEnd)

      endTime = `${String(Math.floor(finalEnd / 60)).padStart(2, '0')}:${String(finalEnd % 60).padStart(2, '0')}`
    }

    setEditingActivity(null)
    setForm({
      date: targetDate,
      title: '', description: '',
      time_start: timeStart || '',
      time_end: endTime,
      activity_type_id: types[0]?.id?.toString() || '',
      neighborhood: '', address: '', location_name: '', notes: '',
      status_type_id: plannedId?.toString() || '',
    })
    setModalOpen(true)
  }

  function openView(activity: Activity) {
    setViewingActivity(activity)
    setViewModalOpen(true)
  }

  function openEditFromView() {
    if (!viewingActivity) return
    setViewModalOpen(false)
    openEdit(viewingActivity)
  }

  function openDeleteFromView() {
    if (!viewingActivity) return
    setViewModalOpen(false)
    openDelete(viewingActivity)
  }

  function openEdit(activity: Activity) {
    setEditingActivity(activity)
    setForm({
      date: activity.date,
      title: activity.title,
      description: activity.description || '',
      time_start: fmtTime(activity.time_start),
      time_end: fmtTime(activity.time_end),
      activity_type_id: activity.activity_type_id.toString(),
      neighborhood: activity.neighborhood || '',
      address: activity.address || '',
      location_name: activity.location_name || '',
      notes: activity.notes || '',
      status_type_id: activity.status_type_id.toString(),
    })
    setModalOpen(true)
  }

  function openDelete(activity: Activity) {
    setEditingActivity(activity)
    setDeleteModalOpen(true)
  }

  // Save
  async function handleSave() {
    if (saving) return
    setSaving(true)
    const payload: any = { ...form }
    if (!payload.time_start) delete payload.time_start
    if (!payload.time_end) delete payload.time_end
    payload.activity_type_id = parseInt(payload.activity_type_id)
    payload.status_type_id = parseInt(payload.status_type_id)

    try {
      if (editingActivity) {
        await axios.put(buildApiUrl(`/v1/agenda/${editingActivity.id}`), payload)
        toast('success', 'Atividade atualizada')
      } else {
        await axios.post(buildApiUrl('/v1/agenda'), payload)
        toast('success', 'Atividade criada')
      }
      setModalOpen(false)
      fetchActivities()
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Erro ao salvar'
      toast('error', msg)
    } finally { setSaving(false) }
  }

  // Delete
  async function handleDelete() {
    if (!editingActivity) return
    try {
      await axios.delete(buildApiUrl(`/v1/agenda/${editingActivity.id}`))
      toast('success', 'Atividade excluída')
      setDeleteModalOpen(false)
      setEditingActivity(null)
      if (dayDetailDate) {
        const remaining = getActivitiesForDate(dayDetailDate).filter(a => a.id !== editingActivity.id)
        if (remaining.length === 0) setDayDetailDate(null)
      }
      fetchActivities()
    } catch {
      toast('error', 'Erro ao excluir')
    }
  }

  function getMapsUrl(a: Activity) {
    if (a.latitude && a.longitude) return `https://www.google.com/maps?q=${a.latitude},${a.longitude}`
    if (a.address && a.city_name) return `https://www.google.com/maps/search/${encodeURIComponent(`${a.address}, ${a.neighborhood || ''}, ${a.city_name}`)}`
    return null
  }

  const today = fmt(new Date())
  const dayActivities = dayDetailDate ? getActivitiesForDate(dayDetailDate) : []

  // Grouped by date for list view
  const groupedByDate = activities.reduce<Record<string, Activity[]>>((acc, a) => {
    if (!acc[a.date]) acc[a.date] = []
    acc[a.date].push(a)
    return acc
  }, {})

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agenda</h1>
          <p className="text-gray-500 mt-1">Organize a campanha dia a dia</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => openCreate()}>
          Nova Atividade
        </Button>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-white text-gray-500 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 min-w-[200px] text-center">
            {MONTHS[month]} {year}
          </h2>
          <button onClick={nextMonth} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-white text-gray-500 transition-colors">
            <ChevronRight className="w-5 h-5" />
          </button>
          <Button variant="outline" size="sm" onClick={goToday}>Hoje</Button>
        </div>

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setView('month')} className={clsx('flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors', view === 'month' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500')}>
            <Calendar className="w-4 h-4" /> Mês
          </button>
          <button onClick={() => setView('list')} className={clsx('flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors', view === 'list' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500')}>
            <List className="w-4 h-4" /> Lista
          </button>
        </div>
      </div>

      {/* Calendar View */}
      {view === 'month' && (
        <Card padding="none">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 border-b border-gray-100">
            {WEEKDAYS.map(d => (
              <div key={d} className="py-3 text-center text-xs font-medium text-gray-500 uppercase">{d}</div>
            ))}
          </div>
          {/* Days */}
          <div className="grid grid-cols-7">
            {getCalendarDays().map(({ date, day, isCurrentMonth }) => {
              const dayActs = getActivitiesForDate(date)
              const isToday = date === today
              return (
                <div
                  key={date}
                  onClick={() => { if (isCurrentMonth) setDayDetailDate(date) }}
                  className={clsx(
                    'min-h-[100px] p-2 border-b border-r border-gray-50 cursor-pointer transition-colors hover:bg-brand-25',
                    !isCurrentMonth && 'bg-gray-25 opacity-50',
                  )}
                >
                  <div className={clsx(
                    'w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium mb-1',
                    isToday ? 'bg-brand-500 text-white' : 'text-gray-700',
                  )}>
                    {day}
                  </div>
                  <div className="space-y-0.5">
                    {dayActs.slice(0, 3).map(a => (
                      <div key={a.id} className="flex items-center gap-1 text-[11px] leading-tight">
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: a.status_color }} />
                        <span className="truncate text-gray-600">
                          {fmtTime(a.time_start)} {a.title}
                        </span>
                      </div>
                    ))}
                    {dayActs.length > 3 && (
                      <p className="text-[10px] text-brand-500 font-medium">+{dayActs.length - 3} mais</p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="space-y-4">
          {Object.keys(groupedByDate).length === 0 && !loading && (
            <Card>
              <EmptyState
                icon={<Calendar className="w-7 h-7" />}
                title="Nenhuma atividade neste mês"
                description="Clique em 'Nova Atividade' para começar a organizar a agenda."
              />
            </Card>
          )}
          {Object.entries(groupedByDate).sort(([a],[b]) => a.localeCompare(b)).map(([date, acts]) => {
            const d = new Date(date + 'T12:00:00')
            const weekday = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'][d.getDay()]
            return (
              <div key={date}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={clsx('w-10 h-10 rounded-xl flex flex-col items-center justify-center text-center flex-shrink-0', date === today ? 'bg-brand-500 text-white' : 'bg-white text-gray-700 border border-gray-200')}>
                    <span className="text-xs font-medium leading-none">{d.getDate()}</span>
                    <span className="text-[9px] uppercase leading-none mt-0.5">{MONTHS[d.getMonth()].slice(0,3)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{weekday}</p>
                    <p className="text-xs text-gray-400">{acts.length} atividade{acts.length > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="space-y-2 ml-[52px]">
                  {acts.map(a => (
                    <ActivityCard key={a.id} activity={a} onView={() => openView(a)} getMapsUrl={getMapsUrl} />
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Day detail panel */}
      <SlidePanel
        open={!!dayDetailDate}
        onClose={() => setDayDetailDate(null)}
        title={dayDetailDate ? new Date(dayDetailDate + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
        subtitle={`${dayActivities.length} atividade${dayActivities.length !== 1 ? 's' : ''}`}
        actions={
          <button onClick={() => openCreate(dayDetailDate!)} className="h-9 w-9 flex items-center justify-center rounded-lg bg-brand-50 text-brand-500 hover:bg-brand-100 transition-colors">
            <Plus className="w-4 h-4" />
          </button>
        }
      >
        <DayTimeline
          date={dayDetailDate!}
          activities={dayActivities}
          onClickTime={(time) => openCreate(dayDetailDate!, time)}
          onView={openView}
          onEdit={openEdit}
          onDelete={openDelete}
          getMapsUrl={getMapsUrl}
        />
      </SlidePanel>

      {/* Create/Edit Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="lg">
        <ModalHeader onClose={() => setModalOpen(false)}>
          {editingActivity ? 'Editar Atividade' : 'Nova Atividade'}
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Data</label>
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Tipo</label>
                <select value={form.activity_type_id} onChange={e => setForm({ ...form, activity_type_id: e.target.value })} className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none appearance-none">
                  {types.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Título</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Caminhada no Jacintinho" className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Início</label>
                <input type="time" value={form.time_start} onChange={e => setForm({ ...form, time_start: e.target.value })} className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Término</label>
                <input type="time" value={form.time_end} onChange={e => setForm({ ...form, time_end: e.target.value })} className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Local</label>
              <input value={form.location_name} onChange={e => setForm({ ...form, location_name: e.target.value })} placeholder="Ex: Mercado do Jacintinho" className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Bairro</label>
                <input value={form.neighborhood} onChange={e => setForm({ ...form, neighborhood: e.target.value })} placeholder="Bairro" className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Endereço</label>
                <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Rua, número" className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none" />
              </div>
            </div>

            {editingActivity && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
                <select value={form.status_type_id} onChange={e => setForm({ ...form, status_type_id: e.target.value })} className="h-11 w-full rounded-lg border border-gray-300 px-4 text-sm shadow-xs focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none appearance-none">
                  {statuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Observações</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} placeholder="Notas adicionais..." className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm shadow-xs placeholder:text-gray-400 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10 outline-none resize-none" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={!form.title || !form.date} loading={saving}>
            {editingActivity ? 'Salvar' : 'Criar'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* View Modal */}
      <Modal open={viewModalOpen} onClose={() => setViewModalOpen(false)} size="md">
        {viewingActivity && (() => {
          const a = viewingActivity
          const TypeIcon = typeIconMap[a.type_code] || FileText
          const mapsUrl = getMapsUrl(a)
          const sMin = a.time_start ? timeToMinutes(a.time_start) : 0
          const eMin = a.time_end ? timeToMinutes(a.time_end) : 0

          return (
            <>
              <div className="flex items-center justify-between p-6 pb-0">
                <h2 className="text-xl font-semibold text-gray-900">Detalhes da Atividade</h2>
                <div className="flex items-center gap-1">
                  <button onClick={openEditFromView} className="h-9 w-9 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-400 hover:text-gray-600" title="Editar">
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={openDeleteFromView} className="h-9 w-9 rounded-full hover:bg-error-50 flex items-center justify-center transition-colors text-gray-400 hover:text-error-500" title="Excluir">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setViewModalOpen(false)} className="h-9 w-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
              <ModalBody>
                <div className="space-y-5">
                  {/* Title + type + status */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <TypeIcon className="w-5 h-5 text-gray-400" />
                      <Badge variant={statusBadge[a.status_code] || 'gray'} dot>{a.status_label}</Badge>
                      <span className="text-sm text-gray-400">{a.type_label}</span>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{a.title}</h3>
                  </div>

                  {/* Info grid */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Date */}
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Data</p>
                      <p className="text-sm font-medium text-gray-800">
                        {new Date(a.date + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    </div>

                    {/* Time */}
                    {a.time_start && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Horário</p>
                        <p className="text-sm font-medium text-gray-800">
                          {a.time_start.substring(0, 5)} - {a.time_end?.substring(0, 5)}
                          <span className="text-gray-400 font-normal ml-1">({formatDuration(sMin, eMin)})</span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Location */}
                  {(a.location_name || a.city_name || a.address || a.neighborhood) && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Local</p>
                      {a.location_name && <p className="text-sm font-medium text-gray-800">{a.location_name}</p>}
                      {a.address && <p className="text-sm text-gray-600 mt-0.5">{a.address}</p>}
                      {a.neighborhood && <p className="text-sm text-gray-500 mt-0.5">{a.neighborhood}</p>}
                      {a.city_name && <p className="text-sm text-gray-500 mt-0.5">{a.city_name}/{a.state_code}</p>}
                      {mapsUrl && (
                        <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand-500 hover:text-brand-600 font-medium mt-3">
                          <MapPin className="w-4 h-4" /> Abrir no mapa
                        </a>
                      )}
                    </div>
                  )}

                  {/* Assigned */}
                  {a.assigned_name && (
                    <div className="bg-gray-50 rounded-xl p-4">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Responsável</p>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-brand-50 rounded-full flex items-center justify-center">
                          <span className="text-xs font-semibold text-brand-600">{a.assigned_name.charAt(0)}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-800">{a.assigned_name}</span>
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {a.description && (
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Descrição</p>
                      <p className="text-sm text-gray-600 leading-relaxed">{a.description}</p>
                    </div>
                  )}

                  {/* Notes */}
                  {a.notes && (
                    <div>
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Observações</p>
                      <p className="text-sm text-gray-600 leading-relaxed italic">{a.notes}</p>
                    </div>
                  )}
                </div>
              </ModalBody>
              <div className="p-6 pt-0" />
            </>
          )
        })()}
      </Modal>

      {/* Delete Modal */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} size="sm">
        <ModalHeader onClose={() => setDeleteModalOpen(false)}>Excluir Atividade</ModalHeader>
        <ModalBody>
          <p className="text-sm text-gray-600">
            Tem certeza que deseja excluir <strong>"{editingActivity?.title}"</strong>? Esta ação não pode ser desfeita.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>Cancelar</Button>
          <Button variant="danger" onClick={handleDelete}>Excluir</Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

// Day Timeline Component
const SLOT_HEIGHT = 60 // px por hora
const MIN_CARD_HEIGHT = 28 // altura mínima do card em px

function timeToMinutes(t: string): number {
  const [h, m] = t.substring(0, 5).split(':').map(Number)
  return h * 60 + m
}

function minutesToTime(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

function formatDuration(startMin: number, endMin: number): string {
  const diff = endMin - startMin
  if (diff < 60) return `${diff}min`
  const h = Math.floor(diff / 60)
  const m = diff % 60
  return m > 0 ? `${h}h${m}min` : `${h}h`
}

function DayTimeline({ date, activities, onClickTime, onView, onEdit, onDelete, getMapsUrl }: {
  date: string
  activities: Activity[]
  onClickTime: (time: string) => void
  onView: (a: Activity) => void
  onEdit: (a: Activity) => void
  onDelete: (a: Activity) => void
  getMapsUrl: (a: Activity) => string | null
}) {
  // Calcular range de horas baseado nas atividades
  const timedActivities = activities.filter(a => a.time_start && a.time_end)
  const noTimeActivities = activities.filter(a => !a.time_start)

  const now = new Date()
  const currentHour = now.getHours()
  const isToday = date === now.toISOString().split('T')[0]

  // Range: 1h antes da primeira atividade até 1h depois da última, mín 06-22
  let firstHour = 6
  let lastHour = 22
  if (timedActivities.length > 0) {
    const earliest = Math.min(...timedActivities.map(a => Math.floor(timeToMinutes(a.time_start!) / 60)))
    const latest = Math.max(...timedActivities.map(a => Math.ceil(timeToMinutes(a.time_end!) / 60)))
    firstHour = Math.max(0, Math.min(earliest - 1, 6))
    lastHour = Math.max(latest + 1, 22)
  }

  const hours = Array.from({ length: lastHour - firstHour + 1 }, (_, i) => i + firstHour)
  const startMinute = firstHour * 60

  function fmtHour(h: number) {
    return `${String(h).padStart(2, '0')}:00`
  }

  return (
    <div className="flex flex-col">
      {/* Activities without time */}
      {noTimeActivities.length > 0 && (
        <div className="px-5 pt-4 pb-2">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Sem horário definido</p>
          <div className="space-y-2">
            {noTimeActivities.map(a => (
              <ActivityCard key={a.id} activity={a} onView={() => onView(a)} getMapsUrl={getMapsUrl} />
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="px-2 py-2">
        <div className="flex">
          {/* Hour labels */}
          <div className="w-14 flex-shrink-0">
            {hours.map(hour => (
              <div key={hour} style={{ height: SLOT_HEIGHT }} className="text-right pr-3 pt-1">
                <span className={clsx('text-xs font-medium', isToday && hour === currentHour ? 'text-brand-500' : 'text-gray-400')}>
                  {fmtHour(hour)}
                </span>
              </div>
            ))}
          </div>

          {/* Grid + activities */}
          <div className="flex-1 relative">
            {/* Grid lines + click zones */}
            {hours.map(hour => (
              <div key={hour} style={{ height: SLOT_HEIGHT }} className="border-t border-gray-100 relative group">
                {/* Now indicator */}
                {isToday && hour === currentHour && (
                  <div className="absolute left-0 right-0 h-0.5 bg-brand-500 z-30" style={{ top: `${(now.getMinutes() / 60) * 100}%` }}>
                    <div className="absolute -left-1 -top-[3px] w-2 h-2 rounded-full bg-brand-500" />
                  </div>
                )}
                {/* Add button */}
                <button
                  onClick={() => onClickTime(fmtHour(hour))}
                  className="absolute inset-0 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <span className="flex items-center gap-1.5 text-xs text-brand-400 bg-brand-50/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm">
                    <Plus className="w-3 h-3" /> {fmtHour(hour)}
                  </span>
                </button>
              </div>
            ))}

            {/* Positioned activity cards */}
            {timedActivities.map(a => {
              const sMin = timeToMinutes(a.time_start!)
              const eMin = timeToMinutes(a.time_end!)
              const durationMin = eMin - sMin
              const top = ((sMin - startMinute) / 60) * SLOT_HEIGHT + 2
              const height = Math.max((durationMin / 60) * SLOT_HEIGHT - 4, MIN_CARD_HEIGHT)
              const isShort = height < 50
              const isTiny = height < 36
              const TypeIcon = typeIconMap[a.type_code] || FileText

              return (
                <div
                  key={a.id}
                  className="absolute left-0 right-3 z-20"
                  style={{ top: `${top}px`, height: `${height}px` }}
                >
                  <div
                    className="h-full rounded-lg border border-gray-200 bg-white hover:shadow-md transition-shadow cursor-pointer overflow-hidden group/card"
                    style={{ borderLeftWidth: 3, borderLeftColor: a.status_color }}
                    onClick={() => onView(a)}
                  >
                    {isTiny ? (
                      <div className="flex items-center gap-2 px-2.5 h-full">
                        <TypeIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span className="text-xs font-medium text-gray-900 truncate">{a.title}</span>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">{a.time_start!.substring(0, 5)}-{a.time_end!.substring(0, 5)}</span>
                        <div className="flex items-center gap-0.5 opacity-0 group-hover/card:opacity-100 transition-opacity flex-shrink-0 ml-auto">
                          <button onClick={e => { e.stopPropagation(); onEdit(a) }} className="h-5 w-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-600">
                            <Edit2 className="w-3 h-3" />
                          </button>
                          <button onClick={e => { e.stopPropagation(); onDelete(a) }} className="h-5 w-5 flex items-center justify-center rounded text-gray-400 hover:text-error-500">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ) : isShort ? (
                      <div className="px-3 py-1.5">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <TypeIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-900 truncate">{a.title}</span>
                          </div>
                          <div className="flex items-center gap-0.5 opacity-0 group-hover/card:opacity-100 transition-opacity flex-shrink-0">
                            <button onClick={e => { e.stopPropagation(); onEdit(a) }} className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button onClick={e => { e.stopPropagation(); onDelete(a) }} className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:text-error-500 hover:bg-error-50">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-0.5">
                          <span>{a.time_start!.substring(0, 5)} - {a.time_end!.substring(0, 5)}</span>
                          <span className="text-gray-300">({formatDuration(sMin, eMin)})</span>
                          {a.location_name && <span className="truncate">{a.location_name}</span>}
                        </div>
                      </div>
                    ) : (
                      <div className="px-3 py-2 h-full flex flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <TypeIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-900 truncate">{a.title}</span>
                          </div>
                          <div className="flex items-center gap-0.5 opacity-0 group-hover/card:opacity-100 transition-opacity flex-shrink-0">
                            <button onClick={e => { e.stopPropagation(); onEdit(a) }} className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button onClick={e => { e.stopPropagation(); onDelete(a) }} className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:text-error-500 hover:bg-error-50">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-1">
                          <Clock className="w-3 h-3 flex-shrink-0" />
                          <span>{a.time_start!.substring(0, 5)} - {a.time_end!.substring(0, 5)}</span>
                          <span className="text-gray-300">({formatDuration(sMin, eMin)})</span>
                        </div>
                        {a.location_name && (
                          <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{a.location_name}{a.neighborhood ? `, ${a.neighborhood}` : ''}</span>
                          </div>
                        )}
                        {height > 100 && a.assigned_name && (
                          <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-1">
                            <User className="w-3 h-3 flex-shrink-0" />
                            <span>{a.assigned_name}</span>
                          </div>
                        )}
                        {height > 120 && a.notes && (
                          <p className="text-[11px] text-gray-400 mt-2 line-clamp-2 italic">{a.notes}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// Activity Card Component (list view - click to view)
function ActivityCard({ activity: a, onView, getMapsUrl }: {
  activity: Activity
  onView: () => void
  getMapsUrl: (a: Activity) => string | null
}) {
  const TypeIcon = typeIconMap[a.type_code] || FileText

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow cursor-pointer" onClick={onView}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <TypeIcon className="w-4 h-4 text-gray-400" />
            <Badge variant={statusBadge[a.status_code] || 'gray'} dot size="sm">{a.status_label}</Badge>
            <span className="text-xs text-gray-400">{a.type_label}</span>
          </div>
          <h4 className="text-sm font-semibold text-gray-900 mb-1">{a.title}</h4>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
            {a.time_start && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {a.time_start.substring(0, 5)}{a.time_end ? ` - ${a.time_end.substring(0, 5)}` : ''}
              </span>
            )}
            {(a.location_name || a.city_name) && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {a.location_name || ''}{a.city_name ? `${a.location_name ? ', ' : ''}${a.city_name}/${a.state_code}` : ''}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0 mt-1" />
      </div>
    </div>
  )
}
